import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import PageShell from "../components/marketing/PageShell";
import { api } from "../lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!sessionId) { setError(true); return; }
    let tries = 0;
    let cancel = false;
    const poll = async () => {
      try {
        const r = await api.get(`/orders/by-session/${sessionId}`);
        if (cancel) return;
        setOrder(r.data);
        if (r.data.payment_status !== "paid" && tries < 8) {
          tries += 1;
          setTimeout(poll, 1500);
        }
      } catch {
        setError(true);
      }
    };
    poll();
    return () => { cancel = true; };
  }, [sessionId]);

  const paid = order?.payment_status === "paid";

  return (
    <PageShell testid="page-checkout-success">
      <SEO title="Payment Success" description="Thanks for your purchase — your BRep order is confirmed." path="/checkout/success" />
      <Nav />
      <section className="min-h-[70vh] flex items-center px-6 md:px-12 py-24">
        <div className="max-w-2xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="border border-white/10 p-8 md:p-12"
            data-testid="checkout-success-card"
          >
            <div className="flex items-center gap-3 text-[#2A5BFF]">
              <CheckCircle2 size={22} />
              <span className="text-[11px] tracking-[0.32em] uppercase">
                {paid ? "Payment Received" : "Confirming Payment…"}
              </span>
            </div>
            <h1 className="mt-6 font-anton uppercase text-4xl md:text-5xl leading-[0.95]">
              {paid ? "Thanks — you're all set." : "Almost there."}
            </h1>
            {order && (
              <div className="mt-8 space-y-2 text-sm text-white/70" data-testid="checkout-success-details">
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/40 uppercase tracking-[0.22em] text-[10px]">Service</span>
                  <span>{order.service_title}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/40 uppercase tracking-[0.22em] text-[10px]">Amount</span>
                  <span>{(order.currency || "").toUpperCase()} {Number(order.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/40 uppercase tracking-[0.22em] text-[10px]">Order</span>
                  <span className="font-mono text-xs">{order.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40 uppercase tracking-[0.22em] text-[10px]">Status</span>
                  <span className={paid ? "text-[#2A5BFF]" : "text-white/70"} data-testid="checkout-success-status">
                    {order.payment_status}
                  </span>
                </div>
              </div>
            )}
            {error && (
              <p className="mt-6 text-white/60" data-testid="checkout-success-error">
                Couldn't fetch order details. If your bank confirmed the payment, contact hello@thebrep.app.
              </p>
            )}
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.24em] uppercase" data-testid="checkout-success-dashboard">
                Open Dashboard
              </Link>
              <Link to="/services" className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.24em] uppercase" data-testid="checkout-success-services">
                View Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </PageShell>
  );
}
